import { useState, useEffect, useRef } from 'react';
import { FieldProps } from '@firecms/core';
import { BannersField } from './BannersField';

type ThemeMap = Record<string, unknown>;

export function ThemeField({ value, setValue, property }: FieldProps<ThemeMap>) {
    const valueRef = useRef(value);
    valueRef.current = value;

    const [text, setText] = useState(() => JSON.stringify(value ?? {}, null, 2));
    const [parseError, setParseError] = useState<string | null>(null);

    useEffect(() => {
        if (value !== undefined && !parseError) {
            const formatted = JSON.stringify(value, null, 2);
            if (formatted !== text) setText(formatted);
        }
    }, []);

    const handleJsonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const raw = e.target.value;
        setText(raw);
        try {
            setValue(JSON.parse(raw));
            setParseError(null);
        } catch {
            setParseError('Invalid JSON');
        }
    };

    const handleFormat = () => {
        try {
            const parsed = JSON.parse(text);
            const formatted = JSON.stringify(parsed, null, 2);
            setText(formatted);
            setParseError(null);
        } catch {
            setParseError('Invalid JSON — cannot format');
        }
    };

    const handleBannersChange = (banners: unknown) => {
        const updated = { ...(valueRef.current ?? {}), Banners: banners };
        setValue(updated);
        setText(JSON.stringify(updated, null, 2));
    };

    // Derive title: "Dev Theme" → "Dev Banners", "Production Theme" → "Prod Banners"
    const bannersTitle = property.name?.replace('Theme', 'Banners').replace('Production', 'Prod') ?? 'Banners';

    const banners = (value as any)?.Banners;

    return (
        <div className="flex flex-col gap-6 w-full">
            {/* Visual Banners editor */}
            <div className="flex flex-col gap-2">
                <span className="text-sm font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide text-xs">
                    {bannersTitle}
                </span>
                <BannersField
                    value={banners}
                    setValue={handleBannersChange as any}
                    property={property}
                    propertyKey="Banners"
                    context={null as any}
                    includeDescription
                    underlyingValueHasChanged={false}
                    autoFocus={false}
                    tableMode={false}
                    showError={false}
                    error={null}
                    isSubmitting={false}
                />
            </div>

            {/* Full JSON editor */}
            <div className="flex flex-col gap-2 w-full">
                <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide text-xs">
                        {property.name} — Full JSON
                    </span>
                    <button
                        type="button"
                        onClick={handleFormat}
                        className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300"
                    >
                        Format
                    </button>
                </div>
                <textarea
                    value={text}
                    onChange={handleJsonChange}
                    rows={24}
                    spellCheck={false}
                    className="w-full font-mono text-xs p-3 rounded border border-gray-300 dark:border-gray-600 text-black dark:text-gray-100 bg-gray-50 dark:bg-gray-800 focus:outline-none focus:ring-1 focus:ring-blue-400 resize-y"
                />
                {parseError && <span className="text-xs text-red-500">{parseError}</span>}
            </div>
        </div>
    );
}
